import moment from 'moment';
import React from 'react';
import { Button, Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { CheckCircleFill, Circle } from 'react-bootstrap-icons';
import CircularProgressBar from '../circular-progress';
import { Calendar, CalendarCheck, Clock, Copy, Edit2, MapPin, Trash2, User } from 'lucide-react';
import useTaskCard from '../../hooks/useTaskCard';
import {
  formatAssigneeName,
  getOrderTypeColor,
  getTaskDisplayTitle,
  getTaskStatusBadge
} from './taskCardUtils';
import { getFullAddress } from '../../helpers/global';
import QualiaSentConfirmModal from './QualiaSentConfirmModal';
import TaskDescriptionClamp from './TaskDescriptionClamp';

const OrderTypeBadge = ({ orderType }) => {
  if (!orderType) return null;
  const orderTypeColor = getOrderTypeColor(orderType);
  return (
    <span
      className="xtinyFont font-weight-bold rounded-pill"
      style={{ background: orderTypeColor, color: 'white', padding: '1px 8px' }}
    >
      {orderType}
    </span>
  );
};

const TaskSearchItem = ({
  task,
  onStatusUpdate,
  onEditClick,
  onTaskDelete,
  fromDetails = false,
  isHighlighted = false,
  isKanbanView = false,
  showDescription = false
}) => {
  const {
    isClientUser,
    canDeleteTask,
    updatingStatus,
    toggleTaskStatus,
    bizDaysLabel,
    assignedProcessors,
    orderType,
    openServiceInNewTab,
    isQualiaTask,
    showQualiaConfirm,
    confirmQualiaSent,
    cancelQualiaConfirm,
    copyQualiaMessage
  } = useTaskCard({ task, onStatusUpdate, fromDetails });

  const statusBadge = getTaskStatusBadge(task, isKanbanView);
  const propertyAddress = getFullAddress(task?.order?.service);

  const renderTooltip = props => (
    <Tooltip {...props}>
      {assignedProcessors.slice(2).map(p => (
        <React.Fragment key={p._id || p.name}>
          {p.name}
          <br />
        </React.Fragment>
      ))}
    </Tooltip>
  );

  const RenderAssignedTo = () => {
    if (assignedProcessors.length === 0) return null;

    if (assignedProcessors.length === 1) {
      return <span>{formatAssigneeName(assignedProcessors[0].name)}</span>;
    }

    if (assignedProcessors.length < 3) {
      return <span>{assignedProcessors.map(a => formatAssigneeName(a.name)).join(' & ')}</span>;
    }

    return (
      <span>
        {assignedProcessors
          .slice(0, 2)
          .map(a => formatAssigneeName(a.name))
          .join(', ')}{' '}
        &amp;{' '}
        <OverlayTrigger placement="right" overlay={renderTooltip}>
          <u>
            {assignedProcessors.length - 2} other
            {assignedProcessors.length - 2 > 1 ? 's' : ''}
          </u>
        </OverlayTrigger>
      </span>
    );
  };

  return (
    <>
      <Card
        className="task-search-item w-100 hover-shadow-lg cursor-pointer"
        style={{
          borderLeft: `4px solid ${statusBadge.cardBorderColor}`,
          minWidth: 0,
          maxWidth: '100%',
          backgroundColor: isHighlighted ? 'var(--info-light)' : 'white',
          transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
          boxShadow: isHighlighted ? '0 0 0 2px var(--info)' : undefined
        }}
        id={`task-row-${task._id}`}
      >
        <Card.Body className="p-2 position-relative" onClick={openServiceInNewTab}>
          <div className="d-flex">
            {!isClientUser && (
              <div className="text-center pr-2 align-self-start" onClick={e => e.stopPropagation()}>
                {updatingStatus ? (
                  <CircularProgressBar size={1.5} />
                ) : task?.status === 'Complete' ? (
                  <CheckCircleFill size={18} className="text-success hover" onClick={toggleTaskStatus} />
                ) : (
                  <Circle size={18} className="text-secondary hover" onClick={toggleTaskStatus} />
                )}
              </div>
            )}

            <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 0, rowGap: 2, columnGap: 16 }}>
              <div
                className="d-flex justify-content-between align-items-start"
                style={{ gap: 8, minWidth: 0, width: '100%' }}
              >
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      <span>{getTaskDisplayTitle(task)}</span>
                    </Tooltip>
                  }
                >
                  <span
                    className="smallFont font-weight-semibold"
                    style={{
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1
                    }}
                  >
                    {getTaskDisplayTitle(task)}
                  </span>
                </OverlayTrigger>
                <span
                  className="xtinyFont font-weight-bold rounded-pill flex-shrink-0"
                  style={{
                    background: statusBadge.background,
                    color: statusBadge.textColor,
                    padding: '2px 8px'
                  }}
                >
                  {statusBadge.label}
                </span>
              </div>
              <div className="d-flex flex-column my-1" style={{ minWidth: 0, rowGap: 2 }}>
                <div
                  className="d-flex align-items-center flex-wrap text-light-grey-2 tinyFont"
                  style={{ rowGap: 2, columnGap: 16, minWidth: 0 }}
                >
                  {assignedProcessors.length > 0 && (
                    <span className="d-flex align-items-center" style={{ gap: 4 }}>
                      <User size={13} className="flex-shrink-0" />
                      <RenderAssignedTo />
                    </span>
                  )}
                  <span className="d-flex align-items-center" style={{ gap: 4 }}>
                    <CalendarCheck size={13} className="flex-shrink-0" />
                    Due {moment(task.dueDate).format('MMM D')}
                  </span>
                  <span className="d-flex align-items-center" style={{ gap: 4 }}>
                    <Calendar size={13} className="flex-shrink-0" />
                    Created {moment(task.createdAt).format('MMM D')}
                  </span>
                  <span className="d-flex align-items-center" style={{ gap: 4 }}>
                    <Clock size={13} className="flex-shrink-0" />
                    {bizDaysLabel}
                  </span>
                  {!propertyAddress && <OrderTypeBadge orderType={orderType} />}
                </div>

                <div className="d-flex align-items-center flex-wrap" style={{ minWidth: 0, rowGap: 4, columnGap: 8 }}>
                  {propertyAddress && (
                    <>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            <span>{propertyAddress}</span>
                          </Tooltip>
                        }
                      >
                        <div className="text-muted mb-0 d-flex align-items-center text-truncate">
                          <MapPin size={13} className="mr-1 flex-shrink-0" />
                          <span className="xsmallFont text-truncate">{propertyAddress}</span>
                        </div>
                      </OverlayTrigger>
                      <OrderTypeBadge orderType={orderType} />
                    </>
                  )}
                </div>

                {showDescription && <TaskDescriptionClamp task={task} />}
              </div>
            </div>
          </div>

          {!isClientUser && (
            <div className="task-search-item-actions" onClick={e => e.stopPropagation()}>
              {isQualiaTask && (
                <Button
                  size="sm"
                  variant="secondary"
                  className="d-flex align-items-center justify-content-center hover-muted border-0 shadow-none rounded-sm p-1"
                  onClick={copyQualiaMessage}
                  title="Copy"
                >
                  <Copy size={16} className="flex-shrink-0" />
                </Button>
              )}
              <Button
                size="sm"
                variant="info"
                className="d-flex align-items-center justify-content-center bg-info hover-muted border-0 shadow-none rounded-sm p-1"
                onClick={() => onEditClick(task)}
              >
                <Edit2 size={16} className="flex-shrink-0 text-white" />
              </Button>
              {canDeleteTask && (
                <Button
                  size="sm"
                  variant="danger"
                  className="d-flex align-items-center justify-content-center bg-danger hover-muted border-0 shadow-none rounded-sm p-1"
                  onClick={() => onTaskDelete(task)}
                >
                  <Trash2 size={16} className="flex-shrink-0 text-white" />
                </Button>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      <QualiaSentConfirmModal
        show={showQualiaConfirm}
        updating={updatingStatus}
        onConfirm={confirmQualiaSent}
        onCancel={cancelQualiaConfirm}
      />
    </>
  );
};

export default TaskSearchItem;
